import {
  hasValue,
  isFunction,
  localStorage as localStorageHelper,
} from 'swipeclock-frontend';
import { isNativeOfflineShell } from 'helpers/core/url';
import {
  getClockConnectCacheStorageKey,
  getOfflinePunchQueueStorageKey,
} from './webClockUserStorageKey';

const IOS_BRIDGE_TIMEOUT_MS = 3000;
const DEV_STORE_PREFIX = 'WEBCLOCK_DEV_SECURE:';
const DEV_KEY_STORAGE_KEY = 'WEBCLOCK_DEV_SECURE_KEY';
const DEV_ENVELOPE_VERSION = 1;
const PENDING_OFFLINE_CLEAR_STORAGE_KEY = 'WEBCLOCK_PENDING_OFFLINE_CLEAR';

/** Sentinel pending-clear value when no per-user key is available. */
export const PENDING_OFFLINE_CLEAR_ALL = '__ALL__';

const iosMemoryCache = new Map();
const iosPendingRequests = new Map();
const devSecureMemoryCache = new Map();
let devSecureCryptoKeyPromise = null;

const hasAndroidSecureBridge = () =>
  isFunction(window?.AndroidNative?.getWebClockSecureItem) &&
  isFunction(window?.AndroidNative?.setWebClockSecureItem) &&
  isFunction(window?.AndroidNative?.removeWebClockSecureItem) &&
  isFunction(window?.AndroidNative?.clearWebClockSecureItemsForUser) &&
  isFunction(window?.AndroidNative?.clearAllWebClockSecureItems);

const hasAndroidBulkClearBridge = () =>
  isFunction(window?.AndroidNative?.clearAllWebClockSecureItems);

const hasIosSecureBridge = () =>
  isFunction(
    window?.webkit?.messageHandlers?.webClockSecureStorage?.postMessage
  );

const isDevSecureFallbackEnabled = () => process.env.NODE_ENV !== 'production';

/**
 * Selects which WebView secure-storage adapter to use for offline WebClock data.
 * Hosted webviews stay on localStorage. The secure-storage bridge and
 * fail-closed path run only for a native offline-shell load.
 * Values: localStorage | ios | android | devSecure | failClosed.
 */
const resolveWebViewSecureStorageBackend = () => {
  if (!isNativeOfflineShell()) {
    return 'localStorage';
  }

  if (hasIosSecureBridge()) {
    return 'ios';
  }

  if (hasAndroidSecureBridge()) {
    return 'android';
  }

  if (isDevSecureFallbackEnabled()) {
    return 'devSecure';
  }

  return 'failClosed';
};

export const isOfflineSecureStorageWritable = () => {
  const webViewSecureStorageBackend = resolveWebViewSecureStorageBackend();
  return webViewSecureStorageBackend !== 'failClosed';
};

const readLocalStorageValue = (storageKey) => {
  if (!localStorageHelper.isLocalStorageAvailable()) {
    return null;
  }

  const raw = localStorageHelper.getItem(storageKey);
  return hasValue(raw) ? raw : null;
};

const writeLocalStorageValue = (storageKey, value) => {
  if (!localStorageHelper.isLocalStorageAvailable()) {
    return false;
  }

  try {
    localStorageHelper.setItem(storageKey, value);
    return true;
  } catch (error) {
    console.warn('[offlineWebClockStorage] localStorage write failed.', error);
    return false;
  }
};

const removeLocalStorageValue = (storageKey) => {
  if (!localStorageHelper.isLocalStorageAvailable()) {
    return false;
  }

  localStorageHelper.removeItem(storageKey);
  return true;
};

const getSubtleCrypto = () => {
  if (window.crypto?.subtle) {
    return window.crypto.subtle;
  }

  if (globalThis.crypto?.subtle) {
    return globalThis.crypto.subtle;
  }

  return null;
};

const getCryptoRandomValues = () => {
  if (typeof window.crypto?.getRandomValues === 'function') {
    return (array) => window.crypto.getRandomValues(array);
  }

  if (typeof globalThis.crypto?.getRandomValues === 'function') {
    return (array) => globalThis.crypto.getRandomValues(array);
  }

  return null;
};

const bytesToBase64 = (bytes) => {
  let binary = '';
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return btoa(binary);
};

const base64ToBytes = (base64) =>
  Uint8Array.from(atob(base64), (char) => char.charCodeAt(0));

const isEncryptedEnvelope = (raw) => {
  if (!hasValue(raw) || typeof raw !== 'string') {
    return false;
  }

  try {
    const parsed = JSON.parse(raw);
    return (
      parsed?.v === DEV_ENVELOPE_VERSION &&
      hasValue(parsed?.iv) &&
      hasValue(parsed?.ct)
    );
  } catch {
    return false;
  }
};

const getOrCreateDevSecureCryptoKey = async () => {
  if (devSecureCryptoKeyPromise) {
    return devSecureCryptoKeyPromise;
  }

  devSecureCryptoKeyPromise = (async () => {
    const subtle = getSubtleCrypto();

    if (!window.sessionStorage || !subtle) {
      throw new Error('Dev secure storage crypto unavailable.');
    }

    const existing = window.sessionStorage.getItem(DEV_KEY_STORAGE_KEY);

    if (hasValue(existing)) {
      const raw = base64ToBytes(existing);
      return subtle.importKey('raw', raw, { name: 'AES-GCM' }, false, [
        'encrypt',
        'decrypt',
      ]);
    }

    const key = await subtle.generateKey(
      { name: 'AES-GCM', length: 256 },
      true,
      ['encrypt', 'decrypt']
    );
    const exported = await subtle.exportKey('raw', key);
    window.sessionStorage.setItem(
      DEV_KEY_STORAGE_KEY,
      bytesToBase64(new Uint8Array(exported))
    );
    return key;
  })().catch((error) => {
    devSecureCryptoKeyPromise = null;
    throw error;
  });

  return devSecureCryptoKeyPromise;
};

const readDevSecureValue = (storageKey) =>
  devSecureMemoryCache.get(storageKey) ?? null;

const writeDevSecureValue = (storageKey, value) => {
  const subtle = getSubtleCrypto();
  const getRandomValues = getCryptoRandomValues();

  if (!window.sessionStorage || !subtle || !getRandomValues) {
    return false;
  }

  return (async () => {
    try {
      const key = await getOrCreateDevSecureCryptoKey();
      const iv = getRandomValues(new Uint8Array(12));
      const encoded = new TextEncoder().encode(value);
      const cipherBuffer = await subtle.encrypt(
        { name: 'AES-GCM', iv },
        key,
        encoded
      );
      const envelope = JSON.stringify({
        v: DEV_ENVELOPE_VERSION,
        iv: bytesToBase64(iv),
        ct: bytesToBase64(new Uint8Array(cipherBuffer)),
      });
      window.sessionStorage.setItem(
        `${DEV_STORE_PREFIX}${storageKey}`,
        envelope
      );
      devSecureMemoryCache.set(storageKey, value);
      return true;
    } catch (error) {
      console.warn('[offlineWebClockStorage] Dev secure write failed.', error);
      return false;
    }
  })();
};

const removeDevSecureValue = (storageKey) => {
  devSecureMemoryCache.delete(storageKey);

  if (!window.sessionStorage) {
    return false;
  }

  window.sessionStorage.removeItem(`${DEV_STORE_PREFIX}${storageKey}`);
  return true;
};

const hydrateDevSecureValue = async (storageKey) => {
  if (!window.sessionStorage) {
    return null;
  }

  const raw = window.sessionStorage.getItem(`${DEV_STORE_PREFIX}${storageKey}`);

  if (!hasValue(raw)) {
    return null;
  }

  if (!isEncryptedEnvelope(raw)) {
    // Invalid envelope — do not rehydrate.
    window.sessionStorage.removeItem(`${DEV_STORE_PREFIX}${storageKey}`);
    return null;
  }

  const subtle = getSubtleCrypto();

  if (!subtle) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw);
    const key = await getOrCreateDevSecureCryptoKey();
    const plainBuffer = await subtle.decrypt(
      { name: 'AES-GCM', iv: base64ToBytes(parsed.iv) },
      key,
      base64ToBytes(parsed.ct)
    );
    const value = new TextDecoder().decode(plainBuffer);
    devSecureMemoryCache.set(storageKey, value);
    return value;
  } catch (error) {
    console.warn('[offlineWebClockStorage] Dev secure hydrate failed.', error);
    window.sessionStorage.removeItem(`${DEV_STORE_PREFIX}${storageKey}`);
    return null;
  }
};

const clearDevSecureStore = () => {
  devSecureMemoryCache.clear();
  devSecureCryptoKeyPromise = null;

  if (!window.sessionStorage) {
    return false;
  }

  const removedData = removeStorageKeysByPrefix(
    window.sessionStorage,
    DEV_STORE_PREFIX
  );
  window.sessionStorage.removeItem(DEV_KEY_STORAGE_KEY);
  return removedData;
};

const readAndroidSecureValue = (storageKey) =>
  window.AndroidNative.getWebClockSecureItem(storageKey);

const writeAndroidSecureValue = (storageKey, value) =>
  Boolean(window.AndroidNative.setWebClockSecureItem(storageKey, value));

const removeAndroidSecureValue = (storageKey) => {
  if (!isFunction(window?.AndroidNative?.removeWebClockSecureItem)) {
    return false;
  }

  return Boolean(window.AndroidNative.removeWebClockSecureItem(storageKey));
};

const clearAndroidSecureValuesForUser = (userKey) => {
  if (!isFunction(window?.AndroidNative?.clearWebClockSecureItemsForUser)) {
    return false;
  }

  return Boolean(window.AndroidNative.clearWebClockSecureItemsForUser(userKey));
};

const clearAndroidAllSecureValues = () => {
  if (!hasAndroidBulkClearBridge()) {
    return false;
  }

  return Boolean(window.AndroidNative.clearAllWebClockSecureItems());
};

const WEBCLOCK_STORAGE_PREFIX = 'WEBCLOCK_';

const removeStorageKeysByPrefix = (storage, prefix) => {
  if (!storage) {
    return false;
  }

  const keysToRemove = [];

  for (let index = 0; index < storage.length; index += 1) {
    const key = storage.key(index);
    if (hasValue(key) && key.startsWith(prefix)) {
      keysToRemove.push(key);
    }
  }

  keysToRemove.forEach((key) => storage.removeItem(key));
  return keysToRemove.length > 0;
};

const clearBrowserWebClockStorageKeys = () => {
  let cleared = false;

  if (localStorageHelper.isLocalStorageAvailable() && window.localStorage) {
    cleared =
      removeStorageKeysByPrefix(window.localStorage, WEBCLOCK_STORAGE_PREFIX) ||
      cleared;
  }

  if (window.sessionStorage) {
    cleared =
      removeStorageKeysByPrefix(window.sessionStorage, DEV_STORE_PREFIX) ||
      cleared;
    if (window.sessionStorage.getItem(DEV_KEY_STORAGE_KEY)) {
      window.sessionStorage.removeItem(DEV_KEY_STORAGE_KEY);
      cleared = true;
    }
  }

  devSecureMemoryCache.clear();
  devSecureCryptoKeyPromise = null;

  return cleared;
};

const settleWriteResult = (result) => {
  if (isFunction(result?.then)) {
    return result.then((written) => Boolean(written)).catch(() => false);
  }

  return Boolean(result);
};

const postIosSecureMessage = (message) =>
  new Promise((resolve) => {
    const requestId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    let settled = false;

    const finish = (result) => {
      if (settled) {
        return;
      }
      settled = true;
      iosPendingRequests.delete(requestId);
      resolve(result);
    };

    const timeoutId = setTimeout(() => {
      finish({ ok: false, value: null });
    }, IOS_BRIDGE_TIMEOUT_MS);

    iosPendingRequests.set(requestId, (result) => {
      clearTimeout(timeoutId);
      finish(result);
    });

    try {
      window.webkit.messageHandlers.webClockSecureStorage.postMessage({
        ...message,
        requestId,
      });
    } catch {
      clearTimeout(timeoutId);
      finish({ ok: false, value: null });
    }
  });

const handleIosSecureStorageResult = (result) => {
  const callback = iosPendingRequests.get(result?.requestId);

  if (!isFunction(callback)) {
    console.debug(
      '[offlineWebClockStorage] Ignoring stale iOS secure-storage result.',
      result?.requestId
    );
    return;
  }

  callback(result);
};

if (
  typeof window !== 'undefined' &&
  !window.receiveWebClockSecureStorageResult
) {
  window.receiveWebClockSecureStorageResult = handleIosSecureStorageResult;
}

const readIosSecureValueSync = (storageKey) =>
  iosMemoryCache.get(storageKey) ?? null;

const writeIosSecureValue = async (storageKey, value) => {
  const result = await postIosSecureMessage({
    action: 'set',
    key: storageKey,
    value,
  });
  const writeSucceeded = Boolean(result?.ok);

  if (writeSucceeded) {
    iosMemoryCache.set(storageKey, value);
  }

  return writeSucceeded;
};

const removeIosSecureValue = async (storageKey) => {
  iosMemoryCache.delete(storageKey);
  const result = await postIosSecureMessage({
    action: 'remove',
    key: storageKey,
  });

  return Boolean(result?.ok);
};

const hydrateIosSecureValue = async (storageKey) => {
  const result = await postIosSecureMessage({
    action: 'get',
    key: storageKey,
  });

  if (result?.ok && hasValue(result.value)) {
    iosMemoryCache.set(storageKey, result.value);
  }

  return result?.value ?? null;
};

const readSecureValue = (storageKey, webViewSecureStorageBackend) => {
  if (webViewSecureStorageBackend === 'android') {
    return readAndroidSecureValue(storageKey);
  }

  if (webViewSecureStorageBackend === 'ios') {
    return readIosSecureValueSync(storageKey);
  }

  if (webViewSecureStorageBackend === 'devSecure') {
    return readDevSecureValue(storageKey);
  }

  return null;
};

const writeSecureValue = (storageKey, value, webViewSecureStorageBackend) => {
  if (webViewSecureStorageBackend === 'android') {
    return writeAndroidSecureValue(storageKey, value);
  }

  if (webViewSecureStorageBackend === 'ios') {
    return writeIosSecureValue(storageKey, value);
  }

  if (webViewSecureStorageBackend === 'devSecure') {
    return writeDevSecureValue(storageKey, value);
  }

  return false;
};

const removeSecureValue = (storageKey, webViewSecureStorageBackend) => {
  if (webViewSecureStorageBackend === 'android') {
    return removeAndroidSecureValue(storageKey);
  }

  if (webViewSecureStorageBackend === 'ios') {
    return removeIosSecureValue(storageKey);
  }

  if (webViewSecureStorageBackend === 'devSecure') {
    return removeDevSecureValue(storageKey);
  }

  return false;
};

export const readOfflineWebClockItem = (storageKey) => {
  if (!hasValue(storageKey)) {
    return null;
  }

  const webViewSecureStorageBackend = resolveWebViewSecureStorageBackend();

  if (webViewSecureStorageBackend === 'localStorage') {
    return readLocalStorageValue(storageKey);
  }

  if (webViewSecureStorageBackend === 'failClosed') {
    return null;
  }

  return readSecureValue(storageKey, webViewSecureStorageBackend);
};

export const writeOfflineWebClockItem = (storageKey, value) => {
  if (!hasValue(storageKey) || !hasValue(value)) {
    return false;
  }

  const webViewSecureStorageBackend = resolveWebViewSecureStorageBackend();

  if (webViewSecureStorageBackend === 'localStorage') {
    return writeLocalStorageValue(storageKey, value);
  }

  if (webViewSecureStorageBackend === 'failClosed') {
    console.warn(
      '[offlineWebClockStorage] Secure bridge unavailable; write blocked.'
    );
    return false;
  }

  return settleWriteResult(
    writeSecureValue(storageKey, value, webViewSecureStorageBackend)
  );
};

export const removeOfflineWebClockItem = (storageKey) => {
  if (!hasValue(storageKey)) {
    return false;
  }

  const webViewSecureStorageBackend = resolveWebViewSecureStorageBackend();

  if (webViewSecureStorageBackend === 'localStorage') {
    return removeLocalStorageValue(storageKey);
  }

  if (webViewSecureStorageBackend === 'failClosed') {
    return removeLocalStorageValue(storageKey);
  }

  removeLocalStorageValue(storageKey);
  return removeSecureValue(storageKey, webViewSecureStorageBackend);
};

/**
 * Clears all offline WebClock secure-storage and browser origin keys when the
 * mobile env channel switches (alpha ↔ prod) and no userKey is available at boot.
 * Native logout is the primary cleanup path; this is the web-layer safety net.
 *
 * Browser key removal is always attempted but does not satisfy success on mobile
 * WebView secure-storage adapters — the resolved secure-storage operation must succeed.
 *
 * @returns {Promise<boolean>} true when secure-storage cleanup succeeded
 */
export const clearAllOfflineWebClockDataForChannelSwitch = async () => {
  clearBrowserWebClockStorageKeys();
  iosMemoryCache.clear();

  const webViewSecureStorageBackend = resolveWebViewSecureStorageBackend();

  if (webViewSecureStorageBackend === 'android') {
    return clearAndroidAllSecureValues();
  }

  if (webViewSecureStorageBackend === 'ios') {
    const result = await postIosSecureMessage({ action: 'clearAll' });
    return Boolean(result?.ok);
  }

  if (webViewSecureStorageBackend === 'devSecure') {
    clearDevSecureStore();
    return true;
  }

  if (webViewSecureStorageBackend === 'failClosed') {
    return false;
  }

  return true;
};

let pendingOfflineWebClockClear = Promise.resolve();

export const waitForPendingOfflineWebClockClear = () =>
  pendingOfflineWebClockClear;

const performClearOfflineWebClockData = async (userKey) => {
  const cacheKey = getClockConnectCacheStorageKey(userKey);
  const queueKey = getOfflinePunchQueueStorageKey(userKey);
  const webViewSecureStorageBackend = resolveWebViewSecureStorageBackend();

  removeLocalStorageValue(cacheKey);
  removeLocalStorageValue(queueKey);

  if (webViewSecureStorageBackend === 'localStorage') {
    return true;
  }

  if (webViewSecureStorageBackend === 'failClosed') {
    // Origin keys were removed above, but native secure deletion was not confirmed.
    return false;
  }

  if (webViewSecureStorageBackend === 'android') {
    if (clearAndroidSecureValuesForUser(userKey)) {
      return true;
    }

    const cacheRemoved = removeSecureValue(
      cacheKey,
      webViewSecureStorageBackend
    );
    const queueRemoved = removeSecureValue(
      queueKey,
      webViewSecureStorageBackend
    );
    return Boolean(cacheRemoved) && Boolean(queueRemoved);
  }

  if (webViewSecureStorageBackend === 'ios') {
    const [, , clearResult] = await Promise.all([
      removeSecureValue(cacheKey, webViewSecureStorageBackend),
      removeSecureValue(queueKey, webViewSecureStorageBackend),
      postIosSecureMessage({ action: 'clearForUser', userKey }),
    ]);

    return Boolean(clearResult?.ok);
  }

  await Promise.all([
    removeSecureValue(cacheKey, webViewSecureStorageBackend),
    removeSecureValue(queueKey, webViewSecureStorageBackend),
  ]);

  return true;
};

export const clearOfflineWebClockData = async (userKey) => {
  if (!hasValue(userKey)) {
    return false;
  }

  const thisClear = pendingOfflineWebClockClear
    .catch(() => undefined)
    .then(() => performClearOfflineWebClockData(userKey));

  pendingOfflineWebClockClear = thisClear.then(
    () => undefined,
    () => undefined
  );

  return thisClear;
};

export const getPendingOfflineWebClockClear = () => {
  try {
    const pendingKey = window.sessionStorage?.getItem?.(
      PENDING_OFFLINE_CLEAR_STORAGE_KEY
    );
    return hasValue(pendingKey) ? pendingKey : null;
  } catch {
    return null;
  }
};

export const setPendingOfflineWebClockClear = (userKey) => {
  if (!hasValue(userKey)) {
    return;
  }

  try {
    const existing = getPendingOfflineWebClockClear();
    let nextKey = userKey;

    if (hasValue(existing) && existing !== userKey) {
      // Escalate so neither outstanding user-specific clear is lost.
      nextKey = PENDING_OFFLINE_CLEAR_ALL;
    }

    if (existing === PENDING_OFFLINE_CLEAR_ALL) {
      nextKey = PENDING_OFFLINE_CLEAR_ALL;
    }

    window.sessionStorage?.setItem?.(
      PENDING_OFFLINE_CLEAR_STORAGE_KEY,
      nextKey
    );
  } catch {
    // sessionStorage may be unavailable; callers still fail closed in-memory.
  }
};

export const clearPendingOfflineWebClockClear = () => {
  try {
    window.sessionStorage?.removeItem?.(PENDING_OFFLINE_CLEAR_STORAGE_KEY);
  } catch {
    // ignore
  }
};

/**
 * Retries a previously failed offline clear recorded in sessionStorage
 * (per-user key or {@link PENDING_OFFLINE_CLEAR_ALL}).
 * @returns {Promise<boolean>} true when no pending clear remains
 */
export const resolvePendingOfflineWebClockClear = async () => {
  const pendingKey = getPendingOfflineWebClockClear();

  if (!hasValue(pendingKey)) {
    return true;
  }

  try {
    const cleared =
      pendingKey === PENDING_OFFLINE_CLEAR_ALL
        ? await clearAllOfflineWebClockDataForChannelSwitch()
        : await clearOfflineWebClockData(pendingKey);

    if (cleared) {
      clearPendingOfflineWebClockClear();
      return true;
    }
  } catch {
    return false;
  }

  return false;
};

export const hydrateOfflineWebClockStorageForUser = async (userKey) => {
  if (!hasValue(userKey)) {
    return;
  }

  const webViewSecureStorageBackend = resolveWebViewSecureStorageBackend();
  const cacheKey = getClockConnectCacheStorageKey(userKey);
  const queueKey = getOfflinePunchQueueStorageKey(userKey);
  const storageKeys = [cacheKey, queueKey];

  if (webViewSecureStorageBackend === 'ios') {
    await Promise.all(
      storageKeys.map((storageKey) => hydrateIosSecureValue(storageKey))
    );
    return;
  }

  if (webViewSecureStorageBackend === 'devSecure') {
    await Promise.all(
      storageKeys.map((storageKey) => hydrateDevSecureValue(storageKey))
    );
  }
};
